import { store } from '../store/configureStore';

/**
 * Get permission by name from the vendor permissions object
 * This checks the master-level permissions (dashboard, map, search, reports, towSheets, newCall)
 */
export const getPermissionByName = (permissionName) => {
  const authInfo = store.getState().authInfo;
  
  if (!authInfo || !authInfo.permissions) {
    return false;
  }
  
  const permissions = authInfo.permissions;
  
  switch (permissionName.toLowerCase()) {
    case 'dashboard':
      return permissions.dashboard || false;
    case 'map':
      return permissions.map || false;
    case 'search':
      return permissions.search || false;
    case 'reports':
      return permissions.reports || false;
    case 'towsheets':
      return permissions.towSheets || false;
    case 'newcall':
      return permissions.newCall || false;
    default:
      return false;
  }
};

/**
 * NEW: Check if a specific report is enabled for the vendor
 * This checks the report-level permissions within the reportPermissions array
 * @param {string} reportType - The report type (e.g., 'ARS_USAGE', 'RESPONSE')
 * @returns {boolean} - True if the report is enabled, false otherwise
 */
export const hasReportPermission = (reportType) => {
  const authInfo = store.getState().authInfo;
  
  // First check if master reports permission is enabled
  if (!authInfo || !authInfo.permissions || !authInfo.permissions.reports) {
    return false;
  }
  
  const reportPermissions = authInfo.permissions.reportPermissions;
  
  // If no report permissions array, deny access
  if (!reportPermissions || !Array.isArray(reportPermissions)) {
    return false;
  }
  
  // Find the specific report permission
  const reportPermission = reportPermissions.find(
    rp => rp.reportType === reportType
  );
  
  // Return whether the report is enabled
  return reportPermission ? reportPermission.enabled : false;
};

/**
 * Get access token from auth info
 */
export const getAccessToken = () => {
  const authInfo = store.getState().authInfo;
  return authInfo && authInfo.access_token ? authInfo.access_token : null;
};

/**
 * Get refresh token from auth info
 */
export const getRefreshToken = () => {
  const authInfo = store.getState().authInfo;
  return authInfo && authInfo.refresh_token ? authInfo.refresh_token : null;
};

/**
 * Get company ID from auth info
 */
export const getCompanyId = () => {
  const authInfo = store.getState().authInfo;
  return authInfo && authInfo.companyId ? authInfo.companyId : null;
};

/**
 * Clear session data (for logout)
 */
export const clearSessionData = () => {
  return {
    type: 'AUTHENTICATE_USER',
    authInfo: {}
  };
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
  const authInfo = store.getState().authInfo;
  return authInfo && authInfo.isAuthenticated === true;
};

/**
 * Set new access and refresh tokens
 * Used by token refresh service
 */
export const setNewTokens = (accessToken, refreshToken) => {
  return {
    type: 'UPDATE_TOKENS',
    accessToken: accessToken,
    refreshToken: refreshToken
  };
};

/**
 * Refresh token action
 * Used to refresh expired access tokens
 */
export const refreshToken = () => {
  const refreshTokenValue = getRefreshToken();
  if (!refreshTokenValue) {
    return Promise.reject('No refresh token available');
  }
  
  // Return the refresh token for the service to use
  return Promise.resolve(refreshTokenValue);
};

/**
 * Get access levels by module
 * Used for admin section permissions
 */
export const getAccessLevelsByModule = (moduleName) => {
  const authInfo = store.getState().authInfo;
  
  if (!authInfo || !authInfo.accessLevels) {
    return null;
  }
  
  const accessLevels = authInfo.accessLevels;
  
  if (Array.isArray(accessLevels)) {
    const moduleAccess = accessLevels.find(
      level => level.module && level.module.toLowerCase() === moduleName.toLowerCase()
    );
    return moduleAccess || null;
  }
  
  return null;
};
